import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ASSET_MAINTENANCE_TYPES, type MaintenanceCard } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { Button } from '../ui/Button';
import { Notice, StatusBadge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import { Field, field } from '../create/Field';
import { IconChecklist } from '../icons';
import { dateOnly, dayInTz } from '../../lib/format';
import { dueChip, dueTitle, MAINTENANCE_TYPE_LABEL } from './assetVocab';
import { useCompleteMaintenance } from './useCompleteMaintenance';

export type MaintenanceRow = {
  id: string;
  type: string;
  label: string | null;
  dueOn: string;
  doneAt: string | Date | null;
  notes: string | null;
  daysUntil: number;
  overdue: boolean;
  card: MaintenanceCard;
};

type Draft = { type: string; label: string; dueOn: string; notes: string };

/**
 * This asset's calendar: what it is owed, and everything it has already had done.
 *
 * One list, not two, and the open items come first — the server orders them that way
 * because "what does this van need" and "what has this van had" are the same question
 * asked from two ends. Completing STAMPS the row rather than removing it: the history is
 * the point, and a service record with the finished work deleted out of it is worth
 * nothing to the person selling the van.
 */
export function AssetMaintenanceCard({
  assetId,
  rows,
  today,
  tz,
  disabled,
}: {
  assetId: string;
  rows: MaintenanceRow[];
  /** The SHOP's today, for the date field's default. */
  today: string;
  /** The SHOP's zone — `done_at` is a timestamp, and its DAY is the shop's, not yours. */
  tz: string;
  /** A retired asset's calendar is history — readable, not writable. */
  disabled?: boolean;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  /** `null` = closed, `{}` with no id = adding, `{id}` = editing that row. */
  const [form, setForm] = useState<(Draft & { id?: string }) | null>(null);

  const upsert = useMutation(
    trpc.asset.maintenanceUpsert.mutationOptions({
      onSuccess: () => {
        setForm(null);
        void qc.invalidateQueries();
      },
    }),
  );
  const complete = useCompleteMaintenance();

  const startAdd = () => setForm({ type: 'service', label: '', dueOn: today, notes: '' });
  const startEdit = (r: MaintenanceRow) =>
    setForm({
      id: r.id,
      type: r.type,
      label: r.label ?? '',
      dueOn: r.dueOn,
      notes: r.notes ?? '',
    });

  // The server's own rule, restated so the button disables instead of the form bouncing:
  // `custom` without a label is not a reminder, it is the word "custom" on a date.
  // ⚠️ The server REJECTS it at Zod input validation — a 400, not the 500 an earlier
  // version of this comment claimed. Mirroring it here is a courtesy, not a safety net.
  const formValid =
    !!form && !!form.dueOn && (form.type !== 'custom' || !!form.label.trim());

  return (
    <div className="space-y-4">
      {upsert.error ? <Notice tone="danger">{upsert.error.message}</Notice> : null}
      {complete.error ? <Notice tone="danger">{complete.error.message}</Notice> : null}

      {rows.length === 0 ? (
        <EmptyState
          icon={IconChecklist}
          title="Nothing scheduled on this one"
          description="Add the service interval, the insurance renewal, the inspection — whatever this asset is owed. They show up on the fleet page as they come due."
        />
      ) : (
        <ul className="divide-y divide-border text-sm">
          {rows.map((r) => {
            const chip = dueChip(r);
            return (
              <li key={r.id} className="flex flex-wrap items-center gap-3 py-2.5">
                <StatusBadge tone={chip.tone}>{chip.label}</StatusBadge>
                <span className="min-w-0 flex-1 truncate">
                  <span className="font-medium">{dueTitle(r)}</span>{' '}
                  <span className="text-xs text-muted-foreground">
                    {r.doneAt
                      ? `due ${dateOnly(r.dueOn)} · done ${dayInTz(r.doneAt, tz)}`
                      : `due ${dateOnly(r.dueOn)}`}
                  </span>
                </span>
                {!disabled && !r.doneAt ? (
                  <>
                    <Button
                      variant="ghost"
                      className="px-2 py-1 text-xs"
                      onClick={() => startEdit(r)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      className="px-2 py-1 text-xs"
                      disabled={complete.pending}
                      onClick={() => complete.markDone(r.id)}
                    >
                      Mark done
                    </Button>
                  </>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {disabled ? (
        <p className="text-xs text-muted-foreground">
          Retired assets keep their record and take no new entries.
        </p>
      ) : form ? (
        <div className="space-y-3 rounded-[var(--radius)] bg-surface-2 p-3">
          <div className="grid gap-2 sm:grid-cols-2">
            <Field label="What">
              <select
                className={field}
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                {ASSET_MAINTENANCE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {MAINTENANCE_TYPE_LABEL[t] ?? t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Due">
              <input
                type="date"
                className={field}
                value={form.dueOn}
                onChange={(e) => setForm({ ...form, dueOn: e.target.value })}
              />
            </Field>
          </div>
          <Field
            label={form.type === 'custom' ? 'Call it what it is' : 'Label (optional)'}
          >
            <input
              className={field}
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder={form.type === 'custom' ? 'Winter tires' : 'Oil + filter'}
            />
          </Field>
          <Field label="Notes">
            <input
              className={field}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Shop, policy number, whatever you'll want to read back"
            />
          </Field>
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={!formValid || upsert.isPending}
              onClick={() =>
                upsert.mutate({
                  assetId,
                  ...(form.id ? { maintenanceId: form.id } : {}),
                  type: form.type as (typeof ASSET_MAINTENANCE_TYPES)[number],
                  label: form.label.trim(),
                  dueOn: form.dueOn,
                  notes: form.notes.trim(),
                  ...idem(),
                })
              }
            >
              {upsert.isPending ? 'Saving…' : form.id ? 'Save' : 'Add'}
            </Button>
            <Button variant="ghost" onClick={() => setForm(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="ghost" onClick={startAdd}>
          Schedule something
        </Button>
      )}
    </div>
  );
}
